import React, { useState, useEffect } from 'react';
import { Handle, Position, NodeResizer } from 'reactflow';
import { SwarmIcon, GraphIcon, FolderIcon } from './Icons';

function GroupNode({ data, selected }) {
  // Use local state only for internal toggle, initialized from prop once
  // This makes it an "uncontrolled" component with a default value
  const [localExpanded, setLocalExpanded] = useState(true);
  
  // Combine: prefer data.expanded prop if explicitly set, otherwise use local state
  const isExpanded = data.expanded !== undefined ? data.expanded : localExpanded;
  
  // Theme detection
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme !== 'light');
    };
    checkTheme();
    
    // Observer for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    
    return () => observer.disconnect();
  }, []);
  
  const getIcon = () => {
    switch (data.subtype) {
      case 'swarm': return <SwarmIcon size={16} color="#F59E0B" />;
      case 'graph': return <GraphIcon size={16} color="#3B82F6" />;
      default: return <FolderIcon size={16} color="#999" />;
    }
  };

  const getTypeLabel = () => {
    switch (data.subtype) {
      case 'swarm': return 'Swarm';
      case 'graph': return 'Graph';
      default: return 'Group';
    }
  };

  const color = data.color || '#6B7280';
  
  // Generate glow color from the actual selected color
  // Convert hex to rgba with 0.4 opacity for glow effect
  const hexToRgba = (hex, alpha) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // Fallback based on subtype if color parsing fails
    return data.subtype === 'swarm' 
      ? `rgba(245, 158, 11, ${alpha})` 
      : data.subtype === 'graph' 
        ? `rgba(59, 130, 246, ${alpha})` 
        : `rgba(107, 114, 128, ${alpha})`;
  };
  
  const glowColor = hexToRgba(color, 0.4);
  
  // Theme-aware colors
  const themeColors = isDarkMode ? {
    containerBg: 'rgba(15, 15, 15, 0.5)',
    labelColor: '#FFF',
    sublabelColor: '#666',
    buttonBg: '#1F1F1F',
    buttonBorder: '#2A2A2A',
    buttonColor: '#999',
    contentBg: 'rgba(31, 31, 31, 0.3)',
    nodeBadgeBg: '#1F1F1F',
    nodeBadgeBorder: '#2A2A2A',
    nodeBadgeColor: '#999',
    descColor: '#666',
    placeholderColor: '#666',
    handleBorder: '#0F0F0F',
    badgeBorder: 'var(--bg-primary)',
  } : {
    containerBg: 'rgba(255, 255, 255, 0.9)',
    labelColor: '#020202',
    sublabelColor: '#666',
    buttonBg: '#f3f4f6',
    buttonBorder: 'rgba(25, 25, 25, 0.15)',
    buttonColor: '#666',
    contentBg: 'rgba(0, 0, 0, 0.03)',
    nodeBadgeBg: '#ffffff',
    nodeBadgeBorder: 'rgba(25, 25, 25, 0.15)',
    nodeBadgeColor: '#666',
    descColor: '#666',
    placeholderColor: '#999',
    handleBorder: '#ffffff',
    badgeBorder: '#ffffff',
  };

  return (
    <div style={{
      padding: '16px',
      borderRadius: '16px',
      border: `2px dashed ${color}`,
      background: themeColors.containerBg,
      backdropFilter: 'blur(8px)',
      width: '100%',
      height: '100%',
      minWidth: '200px',
      minHeight: isExpanded ? '150px' : '60px',
      transition: 'box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: selected 
        ? `0 0 20px ${glowColor}, 0 0 40px ${hexToRgba(color, 0.2)}` 
        : isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.2)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
      fontFamily: 'Figtree, sans-serif',
    }}>
      {/* Resizer */}
      <NodeResizer 
        color={color}
        isVisible={selected}
        minWidth={200}
        minHeight={150}
      />
      
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '48px',
        flexShrink: 0,
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: data.subtype === 'swarm' 
              ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))' 
              : data.subtype === 'graph' 
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.2))' 
                : 'var(--bg-hover)',
            borderRadius: '8px',
            fontSize: '16px',
            border: data.subtype === 'swarm' 
              ? '1px solid rgba(245, 158, 11, 0.3)' 
              : data.subtype === 'graph' 
                ? '1px solid rgba(59, 130, 246, 0.3)' 
                : '1px solid var(--border-primary)',
          }}>
            {getIcon()}
          </div>
          <div>
            <div style={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: themeColors.labelColor,
              lineHeight: '1.3'
            }}>
              {data.label || 'Unnamed Group'}
            </div>
            <div style={{ 
              fontSize: '10px', 
              color: themeColors.sublabelColor,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: '500'
            }}>
              {getTypeLabel()}
            </div>
          </div>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Update local state for uncontrolled mode
            setLocalExpanded(!isExpanded);
            // Notify parent if callback provided (for controlled mode)
            if (data.onToggleExpand) {
              data.onToggleExpand(!isExpanded);
            }
          }}
          style={{
            background: themeColors.buttonBg,
            border: `1px solid ${themeColors.buttonBorder}`,
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '11px',
            color: themeColors.buttonColor,
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'all 0.2s ease'
          }}
        >
          ▶
        </button>
      </div>

      {/* Content Area */}
      {isExpanded && (
        <div style={{
          padding: '16px',
          background: themeColors.contentBg,
          borderRadius: '12px',
          minHeight: '80px',
          border: `1px dashed ${color}40`,
          flex: 1,
          position: 'relative',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 1,
          marginTop: '10px'
        }}>
          {data.childNodes && data.childNodes.length > 0 ? (
            <div style={{ 
              fontSize: '10px', 
              color: themeColors.nodeBadgeColor,
              position: 'absolute',
              top: '8px',
              left: '8px',
              zIndex: 1000,
              background: themeColors.nodeBadgeBg,
              padding: '4px 10px',
              borderRadius: '6px',
              pointerEvents: 'none',
              border: `1px solid ${themeColors.nodeBadgeBorder}`,
              fontFamily: 'JetBrains Mono, monospace',
            }}>
              {data.childNodes.length} node{data.childNodes.length !== 1 ? 's' : ''}
            </div>
          ) : (
            <div style={{ 
              fontSize: '12px', 
              color: themeColors.placeholderColor,
              fontStyle: 'normal',
              textAlign: 'center',
              paddingTop: '24px',
              pointerEvents: 'auto',
            }}>
              Drag agents here
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {isExpanded && data.description && (
        <div style={{
          marginTop: '10px',
          fontSize: '11px',
          color: themeColors.descColor,
          fontStyle: 'normal',
          pointerEvents: 'none',
          lineHeight: '1.5'
        }}>
          {data.description}
        </div>
      )}

      {/* Handles for functional groups */}
      {data.subtype !== 'visual' && (
        <>
          <Handle
            type="target"
            position={Position.Left}
            style={{ 
              background: color,
              width: '14px',
              height: '14px',
              border: `2px solid ${themeColors.handleBorder}`,
              boxShadow: `0 2px 8px ${glowColor}`
            }}
          />
          <Handle
            type="source"
            position={Position.Right}
            style={{ 
              background: color,
              width: '14px',
              height: '14px',
              border: `2px solid ${themeColors.handleBorder}`,
              boxShadow: `0 2px 8px ${glowColor}`
            }}
          />
        </>
      )}

      {/* Node count badge */}
      {data.childNodes && data.childNodes.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '-10px',
          background: color,
          color: 'white',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: '600',
          border: `2px solid ${themeColors.badgeBorder}`,
          boxShadow: isDarkMode ? '0 2px 6px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.15)',
          zIndex: 100,
        }}>
          {data.childNodes.length}
        </div>
      )}
    </div>
  );
}

export default GroupNode;
